import React, { useState, useCallback, useEffect, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { appointmentsApi } from '../services/api';
import { CalendarEvent, Appointment } from '../types';
import AppointmentModal from '../components/AppointmentModal';
import AppointmentDetailModal from '../components/AppointmentDetailModal';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import '../styles/calendar-google.css';
import { useSettings } from '../contexts/SettingsContext';

const Calendar: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [view, setView] = useState<'dayGridMonth' | 'timeGridWeek' | 'timeGridThreeDay' | 'timeGridDay'>('timeGridWeek');
  
  const calendarRef = useRef<FullCalendar>(null);
  const queryClient = useQueryClient();
  const { isWorkWeek } = useSettings();

  // Set fixed time range from 7 AM to 10 PM
  const getOptimalTimeRange = () => {
    // Return extended range to allow scrolling
    return { min: '05:00:00', max: '23:00:00' };
  };

  const timeRange = getOptimalTimeRange();

  // Handle window resize 
  useEffect(() => {
    const handleResize = () => {
      if (calendarRef.current) {
        // Keep extended range
        const newTimeRange = { min: '05:00:00', max: '23:00:00' };

        const calendarApi = calendarRef.current.getApi();
        calendarApi.setOption('slotMinTime', newTimeRange.min);
        calendarApi.setOption('slotMaxTime', newTimeRange.max);
        // Ensure we maintain scroll position at 8 AM
        calendarApi.scrollToTime('08:00:00');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [view]);

  // Handle date navigation from URL params
  useEffect(() => {
    const dateParam = searchParams.get('date');
    const viewParam = searchParams.get('view');
    
    if (calendarRef.current) {
      // Change view if specified
      if (viewParam && ['dayGridMonth', 'timeGridWeek', 'timeGridThreeDay', 'timeGridDay'].includes(viewParam)) {
        setView(viewParam as 'dayGridMonth' | 'timeGridWeek' | 'timeGridThreeDay' | 'timeGridDay');
        calendarRef.current.getApi().changeView(viewParam);
      }
      
      // Navigate to date if specified
      if (dateParam) {
        const targetDate = new Date(dateParam);
        if (!isNaN(targetDate.getTime())) {
          calendarRef.current.getApi().gotoDate(targetDate);
        }
      }
    }
  }, [searchParams]);

  // Handle mini calendar date selection events
  useEffect(() => {
    const handleMiniCalendarSelect = (event: CustomEvent) => {
      const { date, view: targetView } = event.detail;
      
      if (calendarRef.current) {
        // Change to week view if specified
        if (targetView && targetView !== view) {
          setView(targetView);
          calendarRef.current.getApi().changeView(targetView);
        }
        
        // Navigate to the selected date
        calendarRef.current.getApi().gotoDate(date);
      }
    };

    window.addEventListener('miniCalendarDateSelect', handleMiniCalendarSelect as EventListener);
    
    return () => {
      window.removeEventListener('miniCalendarDateSelect', handleMiniCalendarSelect as EventListener);
    };
  }, [view]);



  // Fetch calendar events
  const { data: events = [], isLoading } = useQuery<CalendarEvent[]>(
    ['calendar-events', selectedDate],
    async () => {
      const start = new Date();
      start.setMonth(start.getMonth() - 1);
      const end = new Date();
      end.setMonth(end.getMonth() + 2);
      
      console.log('🔍 Fetching calendar events:', { start: start.toISOString(), end: end.toISOString() });
      
      const response = await appointmentsApi.getCalendar(
        start.toISOString(),
        end.toISOString()
      );
      
      console.log('📥 Calendar events received:', response.data);
      console.log(`Total events: ${Array.isArray(response.data) ? response.data.length : 'Not an array'}`);
      
      return Array.isArray(response.data) ? response.data : [];
    }
  );

  // Create appointment mutation
  const createAppointmentMutation = useMutation(
    (data: Partial<Appointment>) => appointmentsApi.create(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['calendar-events']);
        setIsModalOpen(false);
        setSelectedEvent(null);
      },
    }
  );

  // Update appointment mutation
  const updateAppointmentMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<Appointment> }) =>
      appointmentsApi.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['calendar-events']);
        setIsModalOpen(false);
        setSelectedEvent(null);
      },
    }
  );

  // Delete appointment mutation
  const deleteAppointmentMutation = useMutation(
    (id: string) => appointmentsApi.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['calendar-events']);
        setIsModalOpen(false);
        setSelectedEvent(null);
      },
    }
  );

  const handleDateSelect = useCallback((selectInfo: any) => {
    setSelectedDate(selectInfo.start);
    setSelectedEvent(null);
    setIsModalOpen(true);
  }, []);

  const handleEventClick = useCallback((clickInfo: any) => {
    // Preparar los datos del evento para el modal de detalles
    const eventData = {
      ...clickInfo.event.extendedProps,
      id: clickInfo.event.id,
      title: clickInfo.event.title,
      start: clickInfo.event.start.toISOString(),
      end: clickInfo.event.end?.toISOString() || clickInfo.event.start.toISOString(),
      patientName: clickInfo.event.extendedProps.patient ? 
        `${clickInfo.event.extendedProps.patient.firstName} ${clickInfo.event.extendedProps.patient.lastName}` : 
        clickInfo.event.extendedProps.patientName,
      patientPhone: clickInfo.event.extendedProps.patient?.phone || clickInfo.event.extendedProps.patientPhone,
      patientEmail: clickInfo.event.extendedProps.patient?.email || clickInfo.event.extendedProps.patientEmail,
      doctorName: clickInfo.event.extendedProps.doctorName || clickInfo.event.extendedProps.dentist,
      treatmentType: clickInfo.event.extendedProps.type || clickInfo.event.extendedProps.treatmentType,
    };
    
    setSelectedEvent(eventData);
    setSelectedDate(null);
    setIsDetailModalOpen(true);
  }, []);

  const handleEventDrop = useCallback(async (eventDropInfo: any) => {
    const { event } = eventDropInfo;
    const appointmentId = event.id;
    
    try {
      await updateAppointmentMutation.mutateAsync({
        id: appointmentId,
        data: {
          startTime: event.start.toISOString(),
          endTime: event.end.toISOString(),
        },
      });
    } catch (error) {
      // Revert the event position on error
      eventDropInfo.revert();
    }
  }, [updateAppointmentMutation]);

  const handleEventResize = useCallback(async (eventResizeInfo: any) => {
    const { event } = eventResizeInfo;
    const appointmentId = event.id;
    
    try {
      await updateAppointmentMutation.mutateAsync({
        id: appointmentId,
        data: {
          endTime: event.end.toISOString(),
        },
      });
    } catch (error) {
      // Revert the event size on error
      eventResizeInfo.revert();
    }
  }, [updateAppointmentMutation]);

  // Handle view change to update time range
  const handleViewChange = useCallback((viewInfo: any) => {
    const newView = viewInfo.view.type;
    setView(newView);
    
    // Maintain extended range on view change
    if (calendarRef.current) {
      const newTimeRange = { min: '05:00:00', max: '23:00:00' };

      const calendarApi = calendarRef.current.getApi();
      calendarApi.setOption('slotMinTime', newTimeRange.min);
      calendarApi.setOption('slotMaxTime', newTimeRange.max);
      // Scroll to 8 AM when changing views
      calendarApi.scrollToTime('08:00:00');
    }
  }, []);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
    setSelectedDate(null);
  };

  // Converts a datetime-local string (e.g. "2026-06-25T10:00") to an ISO 8601 string
  // with the local timezone offset, so Supabase stores it in the correct time instead of UTC.
  const toLocalISO = (datetimeLocal: string | undefined): string | undefined => {
    if (!datetimeLocal) return undefined;
    const date = new Date(datetimeLocal);
    const pad = (n: number) => String(n).padStart(2, '0');
    const offset = -date.getTimezoneOffset();
    const sign = offset >= 0 ? '+' : '-';
    const absOffset = Math.abs(offset);
    return `${datetimeLocal}:00${sign}${pad(Math.floor(absOffset / 60))}:${pad(absOffset % 60)}`;
  };

  const handleSaveAppointment = async (data: Partial<Appointment>) => {
    if (selectedEvent) {
      // Check if status changed to cancelled - use specific status endpoint for notifications
      const statusChanged = selectedEvent.status !== data.status;
      const isCancellation = data.status === 'cancelled';
      
      if (statusChanged && isCancellation) {
        console.log('🚫 Cancelling appointment from calendar - using status endpoint for notifications');
        try {
          // First update the status using the notification-enabled endpoint
          await appointmentsApi.updateStatus(selectedEvent.id, 'cancelled');
          console.log('✅ Status updated to cancelled');
          
          // Then update other fields if needed
          const { status, patient, ...otherData } = data;
          const transformedData: any = { ...otherData };
          if (patient) transformedData.patientId = patient;
          
          if (Object.keys(transformedData).length > 0) {
            await appointmentsApi.update(selectedEvent.id, transformedData);
          }
          console.log('✅ Appointment fully updated from calendar');
          
          // Refresh data
          queryClient.invalidateQueries({ queryKey: ['appointments'] });
          setIsModalOpen(false);
          setSelectedEvent(null);
        } catch (error) {
          console.error('❌ Error updating appointment from calendar:', error);
        }
      } else {
        // Normal update - transform data for backend
        const updateData: any = {};
        if (data.patient) updateData.patientId = data.patient;
        if (data.title) updateData.title = data.title;
        if (data.description !== undefined) updateData.description = data.description;
        if (data.startTime) updateData.startTime = toLocalISO(data.startTime);
        if (data.endTime) updateData.endTime = toLocalISO(data.endTime);
        if (data.status) updateData.status = data.status;
        if (data.type) updateData.type = data.type;
        if (data.dentist) updateData.dentist = data.dentist;
        if (data.room !== undefined) updateData.room = data.room;
        if (data.notes !== undefined) updateData.notes = data.notes;
        
        await updateAppointmentMutation.mutateAsync({
          id: selectedEvent.id,
          data: updateData,
        });
      }
    } else {
      // Transform data for backend
      const appointmentData: any = {
        patientId: data.patient, // Transform 'patient' to 'patientId'
        title: data.title,
        description: data.description,
        startTime: toLocalISO(data.startTime) || selectedDate?.toISOString(),
        endTime: toLocalISO(data.endTime) || new Date(selectedDate!.getTime() + 60 * 60 * 1000).toISOString(),
        status: data.status,
        type: data.type,
        dentist: data.dentist,
        room: data.room,
        notes: data.notes,
      };
      
      await createAppointmentMutation.mutateAsync(appointmentData);
    }
  };

  const handleDeleteAppointment = async () => {
    if (selectedEvent) {
      await deleteAppointmentMutation.mutateAsync(selectedEvent.id);
    }
  };

  // Funciones para el modal de detalles
  const handleDetailModalClose = () => {
    setIsDetailModalOpen(false);
    setSelectedEvent(null);
  };

  const handleEditFromDetail = () => {
    setIsDetailModalOpen(false);
    setIsModalOpen(true);
  };

  const handleDeleteFromDetail = async () => {
    if (selectedEvent) {
      await deleteAppointmentMutation.mutateAsync(selectedEvent.id);
      setIsDetailModalOpen(false);
      setSelectedEvent(null);
    }
  };

  return (
    <div className="h-full w-full bg-white">
      {isLoading ? (
        <div className="flex items-center justify-center h-full w-full">
          <div className="spinner"></div>
        </div>
      ) : (
        <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView={view}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridThreeDay,timeGridDay'
              }}
              views={{
                timeGridThreeDay: {
                  type: 'timeGrid',
                  duration: { days: 3 },
                  buttonText: '3 Días'
                }
              }}
              locale="es"
              firstDay={1}
              slotDuration="01:00:00"
              slotLabelInterval="01:00:00"
              allDaySlot={false}
              buttonText={{
                today: 'Hoy',
                month: 'Mes',
                week: 'Semana',
                day: 'Día'
              }}
              events={events.map(event => ({
                ...event,
                className: `event-status-${event.status || 'confirmado'}`,
                borderColor: 'transparent',
                textColor: '#ffffff',
                extendedProps: {
                  status: event.status,
                  type: event.type,
                  patient: event.patient,
                  dentist: event.dentist,
                  room: event.room,
                  description: event.description
                }
              }))}
              selectable={true}
              selectMirror={true}
              dayMaxEvents={8}
              weekends={!isWorkWeek}
              hiddenDays={isWorkWeek ? [0, 6] : []}
              select={handleDateSelect}
              eventClick={handleEventClick}
              eventDrop={handleEventDrop}
              eventResize={handleEventResize}
              viewDidMount={handleViewChange}
              eventClassNames={(info) => {
                const status = info.event.extendedProps?.status || 'scheduled';
                return [`event-${status}`];
              }}
              eventContent={(info) => {
                const status = info.event.extendedProps?.status || 'scheduled';
                const statusIcon = status === 'confirmed' ? '✓' : 
                                 status === 'scheduled' ? '📅' : 
                                 status === 'cancelled' ? '✕' : 
                                 status === 'completed' ? '✅' :
                                 status === 'no-show' ? '❌' : '📅';
                
                // Extract patient name and appointment type from title
                const title = info.event.title;
                const titleParts = title.split(' - ');
                const appointmentType = titleParts[0] || '';
                const fullName = titleParts[1] || title;
                
                // Split name into parts
                const nameParts = fullName.split(' ');
                const firstName = nameParts[0] || '';
                const lastName = nameParts[1] || '';
                const secondLastName = nameParts[2] || '';
                
                // Calculate display content based on view and event duration
                const currentView = info.view.type;
                const eventDuration = (info.event.end?.getTime() || 0) - (info.event.start?.getTime() || 0);
                const durationInMinutes = eventDuration / (1000 * 60);
                
                let displayText = '';
                let showTime = true;
                
                // Adapt content based on calendar view and event duration
                if (currentView === 'dayGridMonth') {
                  // Month view: Very compact
                  displayText = `${firstName} ${lastName}`.trim();
                  showTime = false;
                } else if (currentView === 'timeGridWeek' || currentView === 'timeGridThreeDay') {
                  // Week view: Adapt based on duration
                  if (durationInMinutes >= 60) {
                    // Long appointments: Full content
                    displayText = `${appointmentType} - ${firstName} ${lastName} ${secondLastName}`.trim();
                  } else if (durationInMinutes >= 30) {
                    // Medium appointments: Type + Name + First surname
                    displayText = `${appointmentType} - ${firstName} ${lastName}`.trim();
                  } else {
                    // Short appointments: Name + First surname only
                    displayText = `${firstName} ${lastName}`.trim();
                  }
                } else {
                  // Day view: Full content available
                  displayText = `${appointmentType} - ${firstName} ${lastName} ${secondLastName}`.trim();
                }
                
                return {
                  html: `
                    <div class="fc-event-content" style="padding: 2px 4px; overflow: hidden;">
                      <div class="fc-event-title" style="font-size: 11px; line-height: 1.2; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                        <span class="event-icon" style="margin-right: 2px; font-size: 10px;">${statusIcon}</span>
                        <span style="font-weight: 500;">${displayText}</span>
                      </div>
                      ${showTime ? `<div class="fc-event-time" style="font-size: 9px; color: rgba(255,255,255,0.8); margin-top: 1px;">${info.timeText}</div>` : ''}
                    </div>
                  `
                };
              }}
              height="100%"
              // Time Range Settings
              slotMinTime="05:00:00"     // Earliest time visible (5 AM)
              slotMaxTime="23:00:00"     // Latest time visible (11 PM)
              scrollTime="08:00:00"      // Scroll to this time on load (8 AM)
              aspectRatio={1.5}          // Ratio to control height (helps with showing ~8h)
              expandRows={true}
              dayMaxEventRows={true}
              moreLinkClick="popover"
              scrollTimeReset={false}
              stickyHeaderDates={true}
              nowIndicator={true}
              slotEventOverlap={false}
              businessHours={{
                daysOfWeek: [1, 2, 3, 4, 5], // Monday to Friday
                startTime: '09:00',
                endTime: '18:00',
              }}
              eventDisplay="block"
              eventTimeFormat={{
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }}
          />
      )}

      {isModalOpen && (
        <AppointmentModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSave={handleSaveAppointment}
          onDelete={selectedEvent ? handleDeleteAppointment : undefined}
          appointment={selectedEvent}
          selectedDate={selectedDate}
          isLoading={
            createAppointmentMutation.isLoading ||
            updateAppointmentMutation.isLoading ||
            deleteAppointmentMutation.isLoading
          }
        />
      )}

      {isDetailModalOpen && (
        <AppointmentDetailModal
          isOpen={isDetailModalOpen}
          onClose={handleDetailModalClose}
          onEdit={handleEditFromDetail}
          onDelete={handleDeleteFromDetail}
          appointment={selectedEvent}
        />
      )}
    </div>
  );
};

export default Calendar;















